import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { Blog } from '../../types/blogTypes';
import { ArrowRight, Share2, Copy, Check, Info, ChevronDown } from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';

interface FirestoreBlogReadPageProps {
  initialBlog?: Blog | null;
  forcedSlug?: string;
}

const FirestoreBlogReadPage: React.FC<FirestoreBlogReadPageProps> = ({
  initialBlog = null,
  forcedSlug,
}) => {
  const renderFormattedListText = (text: string) => {
    const value = String(text || '').trim();
    const boldWithColon = value.match(/^\*\*(.+?)\*\*:\s*(.*)$/);
    if (boldWithColon) {
      return (
        <>
          <strong>{boldWithColon[1]}:</strong>
          {boldWithColon[2] ? ` ${boldWithColon[2]}` : ''}
        </>
      );
    }

    const boldPrefix = value.match(/^\*\*(.+?)\*\*\s*(.*)$/);
    if (boldPrefix) {
      return (
        <>
          <strong>{boldPrefix[1]}</strong>
          {boldPrefix[2] ? ` ${boldPrefix[2]}` : ''}
        </>
      );
    }

    return value;
  };

  const { slug, id } = useParams<{ slug?: string; id?: string }>();
  const resolvedSlug = String(forcedSlug || slug || id || '').trim().toLowerCase();
  const [blog, setBlog] = useState<Blog | null>(initialBlog);
  const [loading, setLoading] = useState(!initialBlog);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [moreBlogs, setMoreBlogs] = useState<Blog[]>([]);

  useEffect(() => {
    const cleanSlug = resolvedSlug;
    if (!cleanSlug) {
      setError('Missing blog slug.');
      setLoading(false);
      return;
    }

    const fetchBlog = async () => {
      if (!initialBlog) setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getBlogPostBySlug?slug=${encodeURIComponent(cleanSlug)}`
        );
        const result = await response.json();
        if (result?.success && result?.data?.is_firestore_blog) {
          setBlog(result.data as Blog);
          return;
        }
        if (!initialBlog) setError('Blog not found.');
      } catch (fetchError) {
        if (!initialBlog) setError('Unable to load this blog right now.');
      } finally {
        if (!initialBlog) setLoading(false);
      }
    };

    fetchBlog();
  }, [resolvedSlug, initialBlog]);

  useEffect(() => {
    const fetchMoreBlogs = async () => {
      try {
        const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/getBlogPosts?limit=10`);
        const result = await response.json();
        if (!result?.success || !Array.isArray(result?.data)) {
          setMoreBlogs([]);
          return;
        }
        const remotePool = result.data
          .map((item: Partial<Blog> & { slug?: string }) => {
            const id = String(item?.id || item?.slug || '').trim().toLowerCase();
            if (!id || id === resolvedSlug) return null;
            return {
              id,
              slug: id,
              title: String(item?.title || '').trim(),
              excerpt: String(item?.excerpt || '').trim(),
              image: String(item?.image || '').trim(),
              content: String(item?.content || '').trim(),
              category: item?.category === 'landlord' ? 'landlord' : 'tenant',
            } as Blog;
          })
          .filter(Boolean) as Blog[];
        const unique = new Map<string, Blog>();
        remotePool.forEach((item) => {
          if (!item?.id || unique.has(item.id)) return;
          unique.set(item.id, item);
        });
        setMoreBlogs(Array.from(unique.values()).slice(0, 5));
      } catch (e) {
        setMoreBlogs([]);
      }
    };

    fetchMoreBlogs();
  }, [resolvedSlug]);

  const schema = blog?.blog_schema;
  const resolvedTitle = schema?.h1 || blog?.title || '';
  const resolvedSubtitle = schema?.subtitle || blog?.excerpt || '';
  const seoTitle = schema?.seo_title || `${resolvedTitle} | RentSmart.ie Blog`;
  const seoDescription =
    schema?.seo_description ||
    blog?.seoDescription ||
    blog?.excerpt ||
    'Irish property insights from RentSmart.ie';
  const seoKeywords = (schema?.seo_keywords || blog?.seoKeywords || []).join(', ');
  const canonicalUrl = `https://rentsmart.ie/blog/${resolvedSlug}`;
  const shareUrl = useMemo(() => {
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/blog/${resolvedSlug}`;
    }
    return canonicalUrl;
  }, [canonicalUrl, resolvedSlug]);
  const shareTitle = resolvedTitle || 'RentSmart Blog';

  const articleSchema = useMemo(() => {
    if (!blog) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: resolvedTitle,
      description: seoDescription,
      image: blog.image,
      author: {
        '@type': 'Person',
        name: schema?.author?.name || 'RentSmart Research Team',
      },
      datePublished: (blog as Blog & { generated_at?: string }).generated_at || undefined,
      dateModified: (blog as Blog & { updated_at?: string }).updated_at || undefined,
      mainEntityOfPage: canonicalUrl,
    };
  }, [blog, canonicalUrl, resolvedTitle, schema?.author?.name, seoDescription]);

  const faqSchema = useMemo(() => {
    const faqs = schema?.faq || [];
    if (!faqs.length) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    };
  }, [schema?.faq]);

  if (loading) {
    return (
      <div className="relative min-h-screen bg-slate-50 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-sky-500/10 rounded-full blur-[120px] -ml-32 -mb-32" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
          <div className="rounded-[2.5rem] border border-slate-200 bg-white p-8 md:p-10 shadow-sm animate-pulse">
            <div className="flex items-center gap-3 mb-6">
              <span className="relative flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500"></span>
              </span>
              <p className="text-sm font-semibold text-slate-700">
                Loading full article and structured insights...
              </p>
            </div>
            <div className="h-10 w-4/5 rounded-xl bg-slate-200" />
            <div className="mt-4 h-6 w-2/3 rounded-lg bg-slate-200" />
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-8 space-y-4">
                <div className="h-5 w-full rounded bg-slate-200" />
                <div className="h-5 w-11/12 rounded bg-slate-200" />
                <div className="h-5 w-10/12 rounded bg-slate-200" />
                <div className="h-32 w-full rounded-2xl bg-slate-200 mt-4" />
              </div>
              <div className="lg:col-span-4 space-y-4">
                <div className="h-28 w-full rounded-2xl bg-slate-200" />
                <div className="h-24 w-full rounded-2xl bg-slate-200" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return <div className="max-w-6xl mx-auto px-4 py-12">{error || 'Blog not found.'}</div>;
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // no-op
    }
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        {seoKeywords ? <meta name="keywords" content={seoKeywords} /> : null}
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        {blog.image ? <meta property="og:image" content={blog.image} /> : null}
        {articleSchema ? (
          <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        ) : null}
        {faqSchema ? (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        ) : null}
      </Helmet>

      <div className="relative w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden min-h-screen bg-slate-50">
        <section className="bg-slate-900 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] -mr-32 -mt-32" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-sky-500/10 rounded-full blur-[120px] -ml-32 -mb-32" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
            <p className="inline-flex rounded-full border border-amber-300/40 bg-amber-300/10 px-4 py-1 text-xs font-semibold uppercase tracking-wider text-amber-200">
              Estimated Guide - Review with a qualified professional
            </p>
            <h1 className="mt-4 text-3xl md:text-5xl font-black leading-tight">{resolvedTitle}</h1>
            {resolvedSubtitle ? (
              <p className="mt-4 max-w-3xl text-base md:text-lg text-slate-200">{resolvedSubtitle}</p>
            ) : null}
            {schema?.estimated_read_time_minutes ? (
              <p className="mt-4 text-sm text-slate-300">
                Estimated reading time: {schema.estimated_read_time_minutes} minutes
              </p>
            ) : null}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Share:</span>
              <FacebookShareButton url={shareUrl}>
                <FacebookIcon size={32} round />
              </FacebookShareButton>
              <TwitterShareButton url={shareUrl} title={shareTitle}>
                <TwitterIcon size={32} round />
              </TwitterShareButton>
              <WhatsappShareButton url={shareUrl} title={shareTitle}>
                <WhatsappIcon size={32} round />
              </WhatsappShareButton>
              <LinkedinShareButton url={shareUrl}>
                <LinkedinIcon size={32} round />
              </LinkedinShareButton>
              <button
                type="button"
                onClick={copyLink}
                className="ml-1 inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-xs font-bold text-slate-100 hover:bg-white/15"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy Link'}
              </button>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 -mt-8 md:-mt-10 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-6">
            <article className="lg:col-span-8 space-y-8">
              {schema?.introduction ? (
                <section className="bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-100 p-6 md:p-8">
                  <h2 className="text-2xl font-bold text-slate-900">Introduction</h2>
                  <p className="mt-4 text-slate-700 leading-relaxed">{schema.introduction}</p>
                </section>
              ) : null}

              <section className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900">Lets get started</h2>
                {(schema?.content_sections || []).map((section, index) => (
                  <div key={`${section.h2}-${index}`} className="bg-white rounded-[2rem] shadow-sm border border-slate-100 p-6 md:p-8 hover:shadow-md transition-shadow">
                    <h3 className="text-xl font-bold text-slate-900">{section.h2}</h3>
                    <p className="mt-3 text-slate-700">{section.summary}</p>
                    {section.key_points.length ? (
                      <>
                        <h4 className="mt-5 text-sm font-semibold uppercase tracking-wide text-slate-500">Key points</h4>
                        <ul className="mt-3 space-y-2 list-disc list-inside text-slate-700">
                          {section.key_points.map((point) => (
                            <li key={point}>{renderFormattedListText(point)}</li>
                          ))}
                        </ul>
                      </>
                    ) : null}
                    {section.practical_steps.length ? (
                      <>
                        <h4 className="mt-5 text-sm font-semibold uppercase tracking-wide text-slate-500">Practical steps</h4>
                        <ol className="mt-3 space-y-2 list-decimal list-inside text-slate-700">
                          {section.practical_steps.map((step) => (
                            <li key={step}>{renderFormattedListText(step)}</li>
                          ))}
                        </ol>
                      </>
                    ) : null}
                    {section.external_sources.length ? (
                      <div className="mt-6">
                        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
                          <p className="text-sm font-semibold text-emerald-900">Authoritative sources</p>
                          <div className="mt-2 flex flex-col gap-2">
                            {section.external_sources.map((source) => (
                              <a
                                key={`${source.url}-${source.label}`}
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-emerald-700 hover:text-emerald-900 no-underline text-sm"
                              >
                                {source.label}
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ))}
              </section>

              {(schema?.faq || []).length ? (
                <section className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-6 md:p-8">
                  <h2 className="text-2xl font-bold text-slate-900">Frequently Asked Questions</h2>
                  <div className="mt-5 space-y-3">
                    {schema?.faq.map((item, index) => (
                      <details key={`${item.question}-${index}`} className="group rounded-2xl border border-slate-200 p-4">
                        <summary className="cursor-pointer list-none font-semibold text-slate-900 flex items-center justify-between gap-4">
                          <span>{item.question}</span>
                          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition-transform duration-200 group-open:rotate-180">
                            <ChevronDown className="h-4 w-4" />
                          </span>
                        </summary>
                        <p className="mt-3 text-slate-700">{item.answer}</p>
                      </details>
                    ))}
                  </div>
                </section>
              ) : null}

              {schema?.conclusion ? (
                <section className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-6 md:p-8">
                  <h2 className="text-2xl font-bold text-slate-900">Conclusion</h2>
                  <p className="mt-4 text-slate-700 leading-relaxed">{schema.conclusion}</p>
                </section>
              ) : null}

              <section className="bg-white rounded-[2.5rem] border border-slate-100 p-6 md:p-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 mt-0.5">
                      <Share2 className="h-4 w-4" />
                    </div>
                    <div>
                      <h2 className="text-xl font-black text-slate-900">Share This Guide</h2>
                      <p className="text-sm text-slate-600 mt-1">
                        Help others planning a property move in Ireland with this guide.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <FacebookShareButton url={shareUrl}>
                      <FacebookIcon size={34} round />
                    </FacebookShareButton>
                    <TwitterShareButton url={shareUrl} title={shareTitle}>
                      <TwitterIcon size={34} round />
                    </TwitterShareButton>
                    <WhatsappShareButton url={shareUrl} title={shareTitle}>
                      <WhatsappIcon size={34} round />
                    </WhatsappShareButton>
                    <LinkedinShareButton url={shareUrl}>
                      <LinkedinIcon size={34} round />
                    </LinkedinShareButton>
                    <button
                      type="button"
                      onClick={copyLink}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 hover:border-emerald-500 hover:text-emerald-700"
                    >
                      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              </section>

              {moreBlogs.length ? (
                <section className="bg-white rounded-[2.5rem] border border-slate-100 p-6 md:p-8">
                  <h2 className="text-2xl font-black text-slate-900">Discover More Blogs</h2>
                  <p className="mt-2 text-sm text-slate-600">
                    Explore more RentSmart topics related to buying, renting, and property strategy.
                  </p>
                  <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {moreBlogs.map((item) => (
                      <Link
                        key={item.id}
                        to={`/blog/${item.slug || item.id}`}
                        className="no-underline rounded-2xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-emerald-300 transition-colors px-4 py-3 text-sm font-semibold text-slate-800"
                      >
                        {item.title}
                      </Link>
                    ))}
                  </div>
                </section>
              ) : null}
            </article>

            <aside className="lg:col-span-4 space-y-6">
              {(schema?.key_takeaways || []).length ? (
                <section className="lg:sticky lg:top-24 bg-white rounded-[2rem] shadow-sm border border-slate-100 p-6">
                  <h2 className="text-xl font-bold text-slate-900">Key Takeaways</h2>
                  <ul className="mt-4 space-y-3">
                    {schema?.key_takeaways.map((item) => (
                      <li key={item} className="text-sm text-slate-700 border-l-2 border-emerald-500 pl-3">
                        {renderFormattedListText(item)}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {schema?.author ? (
                <section className="bg-white rounded-[2rem] shadow-sm border border-slate-100 p-6">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Author</p>
                  <h3 className="mt-1 text-lg font-bold text-slate-900">{schema.author.name}</h3>
                </section>
              ) : null}

              <section className="bg-slate-900 text-white rounded-[2rem] p-6">
                <h3 className="text-lg font-bold">Explore Homes on RentSmart</h3>
                <p className="mt-2 text-sm text-slate-200">
                  Compare current listings and apply this guide to real market options.
                </p>
                <Link
                  to="/browse"
                  className="inline-flex items-center gap-2 mt-4 rounded-xl bg-emerald-400 px-4 py-2 text-sm font-semibold text-slate-900 no-underline hover:bg-emerald-300"
                >
                  Browse Listings <ArrowRight className="h-4 w-4" />
                </Link>
              </section>

              <section className="bg-amber-50 rounded-[2rem] border border-amber-200 p-5">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-amber-700 mt-0.5" />
                  <div className="text-xs text-amber-900 leading-relaxed space-y-2">
                    <p>
                      This content is provided for general informational purposes only and does not
                      constitute legal, financial, tax, mortgage, investment, or professional advice.
                    </p>
                    <p>
                      RentSmart.ie makes no warranty or guarantee as to completeness, accuracy, or
                      fitness for a particular purpose, and accepts no liability for any loss or
                      damage arising from reliance on this page.
                    </p>
                    <p>
                      Property rules, lending criteria, and market conditions can change. You should
                      independently verify all information and consult qualified professionals before
                      making any property or financial decision.
                    </p>
                  </div>
                </div>
              </section>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default FirestoreBlogReadPage;
